import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Trades from "./Trades";
import Charts from "./Charts";
import Upload from "./Upload";
import Summary from "./Summary";
import axios from "axios";
import Nav from "../components/Nav";
import HeaderStatus from "../components/HeaderStatus";
import Market from "./Market";
import { useAppContext } from "../components/AppContext";
import {
  accentButtonBackground,
  accentButtonText,
  accentButtonTextWeight,
  darkModeBackground,
  darkModeBorder,
  darkModeText,
  darkModeWidgetBackground,
  darkModeWidgetBorderColor,
  lightModeBackground,
  lightModeBorder,
  lightModeText,
  lightModeWidgetBackground,
  lightModeWidgetBorderColor,
} from "../util/style";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { TradeTemplate, Trade, User } from "../types";

interface LoginFields {
  un: string;
  pw: string;
}

const Dashboard: React.FC = () => {
  const {
    location,
    user,
    setUser,
    handleLogout,
    showLoginModal,
    setShowLoginModal,
    maxStartingRefreshToken,
    useApiFetch,
    defaultWatchList,
    getFont,
    allTickerString,
    allAccountString,
    setRefreshToken,
    setResetIdleTrigger,
    darkMode,
    selectedTicker,
    setSelectedTicker,
    setAccountInfo,
    tradesByAccount,
    setTradesByAccount,
    expandChart,
    setExpandChart,
    expandWatchlist,
    setExpandWatchlist,
    setSplitAdjustHistory,
    setSelectedTradePointDetail,
    setBadTickerCallCount,
    lastPrice,
    displayDataParsed,
    tradeTemplate,
    fieldObj,
    setFieldObj,
    replaceMode,
    uploadArray,
    isRunning,
    runServiceHealthCheck,
    resetZoomIndex,
    chartDataSelected,
  } = useAppContext() || {};

  useApiFetch?.({
    url: "/tradeData/getAccountInfo",
    body: { user_id: user?._id },
    onSuccess: (data: { stockAccount?: Record<string, unknown> }) => {
      setAccountInfo?.(data?.stockAccount || {});
    },
    runIfTrue: [!isRunning, !!user?._id],
    deps: [isRunning, user?._id],
  });

  useApiFetch?.({
    url: "/tradeData/getSplitHistory",
    body: {},
    onSuccess: (data: { splitHistory?: Record<string, unknown> }) => {
      setSplitAdjustHistory?.(
        (data?.splitHistory as Record<
          string,
          Record<
            string,
            { date: number; numerator: number; denominator: number }
          >
        >) || {},
      );
    },
    runIfTrue: [!isRunning, !!user?._id],
    deps: [isRunning, user?._id],
  });

  useApiFetch?.({
    url: "/tradeData/getTradeData",
    body: { user_id: user?._id },
    onSuccess: (data: { accounts?: Record<string, Trade[]> }) => {
      setTradesByAccount?.(data?.accounts || {});
    },
    runIfTrue: [!isRunning, !!user?._id],
    deps: [isRunning, user?._id],
  });

  const seedAccountInfo = useCallback(() => {
    const run = async () => {
      try {
        await axios.post("/tradeData/seedAccountInfo", {
          user_id: user?._id,
        });
      } catch (err) {
        runServiceHealthCheck?.();
      }
    };
    run();
  }, [user, runServiceHealthCheck]);

  const handleRefreshTradeData = async () => {
    if (!user?._id) return;
    try {
      const result = await axios.post("/tradeData/getTradeData", {
        user_id: user?._id,
      });
      if (result) {
        setTradesByAccount?.(result?.data?.accounts);
      }
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleRefreshSplitData = async () => {
    try {
      const result = await axios.post("/tradeData/getSplitHistory", {});
      if (result) {
        setSplitAdjustHistory?.(result?.data?.splitHistory);
      }
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleAddTrade = async (formField: TradeTemplate) => {
    if (
      !(
        formField.price &&
        typeof Number(formField.price) === "number" &&
        formField.share &&
        typeof Number(formField.share) === "number" &&
        formField.commission &&
        typeof Number(formField.commission) === "number" &&
        typeof formField.currency === "string" &&
        typeof formField.account === "string" &&
        typeof formField.date === "string" &&
        typeof formField.ticker === "string"
      )
    )
      return;
    try {
      const result = await axios.post("/tradeData/insertTrade", {
        user_id: user?._id,
        trade: formField,
      });
      result && setFieldObj?.(tradeTemplate as TradeTemplate);
      result && handleRefreshTradeData();
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleDeleteTrade = async (_id: number) => {
    try {
      const result = await axios.post("/tradeData/deleteTrade", { _id });
      result && handleRefreshTradeData();
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleReseed = async () => {
    try {
      const result = await axios.post("/tradeData/reloadSeed", {
        _id: user?._id,
      });
      result && handleRefreshTradeData();
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleReloadSplitSeed = async () => {
    try {
      const result = await axios.post("/tradeData/reloadSplitSeed", {});
      result && handleRefreshSplitData();
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };
  const handleUploadJSON = async () => {
    try {
      const result = await axios.post("/tradeData/uploadBulkTrade", {
        tradeArray: uploadArray,
        replace: replaceMode,
        user_id: user?._id,
      });
      result && handleRefreshTradeData();
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleDownloadTrades = async () => {
    try {
      const json = await axios.post("/tradeData/getTradeRaw");

      const getFormattedFilename = (): string => {
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const yyyy = now.getFullYear();
        const hh = String(now.getUTCHours()).padStart(2, "0");
        const min = String(now.getUTCMinutes()).padStart(2, "0");
        const sec = String(now.getUTCSeconds()).padStart(2, "0");
        return `tradesRaw_${mm}_${dd}_${yyyy}_${hh}_${min}_${sec}_GMT.txt`;
      };

      const parsedJSON = json?.data?.data?.map((each: Trade) => ({
        date: each.date,
        ticker: each.ticker,
        price: each.price,
        share: each.share,
        account: each.account,
        currency: each.currency,
        commission: each.commission,
      }));
      const jsonString = JSON.stringify(parsedJSON, null, 2);
      const blob = new Blob([jsonString], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = getFormattedFilename();
      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  const handleAddWatchListTicker = async (ticker: string) => {
    const normalized = ticker.toUpperCase();
    try {
      const result = await axios.post("/stockData/getData", {
        ticker: normalized,
        interval: "90m",
        range: "1d",
      });

      if (result) {
        setBadTickerCallCount?.((prev: Record<string, number>) => {
          const tempObj: Record<string, number> = {};
          Object.keys(prev)
            .filter((each) => each !== normalized)
            .forEach((each) => {
              tempObj[`${each.toUpperCase()}`] = prev[`${each.toUpperCase()}`];
            });
          return tempObj;
        });
      }
    } catch (err) {
      runServiceHealthCheck?.();
    }
  };

  useEffect(() => {
    resetZoomIndex?.();
  }, [chartDataSelected, resetZoomIndex]);

  const [loginFields, setLoginFields] = useState<LoginFields>({
    un: "",
    pw: "",
  });

  const handleLoginFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginFields((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleLoginIn = async () => {
    try {
      const response = await axios.post("/user/login", {
        username: loginFields.un,
        password: loginFields.pw,
      });

      if (response?.data?.success) {
        setShowLoginModal?.(false);
        const userData: User = {
          username: response?.data?.username,
          watchlist: response?.data?.watchlist,
          _id: response?.data?._id,
        };
        setUser?.(userData);
        const sessionData = {
          user: userData,
          expiry: Date.now() + 15 * 60 * 1000,
        };
        localStorage.setItem("tradeSession", JSON.stringify(sessionData));
      } else {
        alert("Login failed. Please check your username and password.");
      }
      setLoginFields({ un: "", pw: "" });
    } catch (error) {
      runServiceHealthCheck?.();
      console.error("Login error:", error);
      alert("Login failed. Please try again later.");
    }
  };

  const handleRegister = async () => {
    try {
      const res = await axios.post("/user/register", {
        username: loginFields.un,
        password: loginFields.pw,
        watchlist: defaultWatchList,
      });

      if (res.data.success) {
        alert("Registration successful! You can now log in.");
        setShowLoginModal?.(false);
      } else {
        alert(res.data.message || "Registration failed.");
      }
    } catch (error) {
      runServiceHealthCheck?.();
      console.error("Registration error:", error);
      alert("An error occurred during registration.");
    }
  };

  const handleWatchListAddRemove = async (ticker: string) => {
    if (!user) return;

    const isInWatchlist = user.watchlist?.includes(ticker);
    const updatedWatchlist = isInWatchlist
      ? user.watchlist.filter((item: string) => item !== ticker)
      : [...(user.watchlist || []), ticker];

    try {
      await axios.post("/user/updateUserWatchlist", {
        _id: user._id,
        watchlist: updatedWatchlist,
      });

      setUser?.((prev: User) => ({ ...prev, watchlist: updatedWatchlist }));
      const oldSession = JSON.parse(
        localStorage.getItem("tradeSession") || "{}",
      );
      if (oldSession) {
        const newSession = {
          ...oldSession,
          user: {
            ...oldSession.user,
            watchlist: updatedWatchlist,
          },
        };
        localStorage.setItem("tradeSession", JSON.stringify(newSession));
      }
    } catch (error) {
      runServiceHealthCheck?.();
      console.error("Error updating watchlist:", error);
      alert("Failed to update watchlist. Please try again later.");
    }
  };

  return (
    <div
      style={{ width: "100vw", height: "100vh" }}
      onClick={() => {
        setRefreshToken?.(maxStartingRefreshToken || 0);
        setResetIdleTrigger?.((prev: number) => prev + 1);
        setSelectedTradePointDetail?.({});
      }}
      className={darkMode ? "darkModeClass" : "lightModeClass"}
    >
      <div
        style={{
          fontFamily: "Arial",
          overflow: "hidden",
          height: "100vh",
          color: darkMode ? darkModeText : lightModeText,
          background: darkMode ? darkModeBackground : lightModeBackground,
          display: "flex",
          flexDirection: "column",
          fontSize: getFont?.(9),
        }}
      >
        <Nav
          handleRefreshTradeData={handleRefreshTradeData}
          handleRefreshSplitData={handleRefreshSplitData}
          handleAddWatchListTicker={handleAddWatchListTicker}
          handleLogout={handleLogout || (() => {})}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {["/market"]?.includes(location?.pathname || "") && <HeaderStatus />}
          <div
            style={{
              flexGrow: 1,
              overflow: "auto",
              height: 0,
            }}
          >
            <Routes>
              <Route
                path="/trades"
                element={<Trades handleDeleteTrade={handleDeleteTrade} />}
              />
              <Route
                path="/upload"
                element={
                  <Upload
                    handleReseed={handleReseed}
                    handleAddTrade={handleAddTrade}
                    handleDownloadTrades={handleDownloadTrades}
                    handleUploadJSON={handleUploadJSON}
                    handleReloadSplitSeed={handleReloadSplitSeed}
                    seedAccountInfo={seedAccountInfo}
                  />
                }
              />
              <Route path="/summary" element={<Summary />} />
              <Route
                path="/market"
                element={
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: expandWatchlist
                          ? "100%"
                          : expandChart
                            ? "0%"
                            : "50%",
                        maxHeight: "calc(100% - 10px)",
                        margin: expandChart
                          ? "0"
                          : expandWatchlist
                            ? "0 10px 10px 10px"
                            : "0 5px 10px 10px",
                        padding: expandChart ? "0" : "0 0px 0 0px",
                        borderRadius: "2px",
                        background: darkMode
                          ? darkModeWidgetBackground
                          : lightModeWidgetBackground,
                        border: `${expandChart ? 0 : 1}px solid ${
                          darkMode
                            ? darkModeWidgetBorderColor
                            : lightModeWidgetBorderColor
                        }`,
                        overflow: "hidden",
                        transition: "none",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      className="hoverShadow"
                    >
                      <div
                        onClick={() => {
                          if (!expandWatchlist) {
                            setExpandChart?.(false);
                          }
                          setExpandWatchlist?.((prev: boolean) => !prev);
                        }}
                        style={{
                          cursor: "pointer",
                          borderRadius: "3px",
                          display: "flex",
                          alignItems: "center",
                          position: "absolute",
                          top: 0,
                          right: 0,
                          zIndex: 5,
                          fontWeight: accentButtonTextWeight,
                          color: accentButtonText,
                          background: accentButtonBackground,
                        }}
                      >
                        {expandWatchlist ? (
                          <KeyboardArrowLeftIcon
                            style={{ fontSize: getFont?.(16) }}
                          />
                        ) : (
                          <KeyboardArrowRightIcon
                            style={{ fontSize: getFont?.(16) }}
                          />
                        )}
                      </div>
                      <div
                        style={{
                          overflowY: expandChart ? "hidden" : "auto",
                          flex: 1,
                        }}
                      >
                        <Market
                          handleWatchListAddRemove={handleWatchListAddRemove}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        position: "relative",
                        width: expandChart
                          ? "100%"
                          : expandWatchlist
                            ? "0%"
                            : "50%",
                        height: "calc(100% - 10px)",
                        margin: expandWatchlist
                          ? "0"
                          : expandChart
                            ? "0 10px 10px 10px"
                            : "0 10px 10px 5px",
                        padding: expandWatchlist ? "0" : "10px",
                        borderRadius: "2px",
                        background: darkMode
                          ? darkModeWidgetBackground
                          : lightModeWidgetBackground,
                        border: `${expandWatchlist ? 0 : 1}px solid ${
                          darkMode
                            ? darkModeWidgetBorderColor
                            : lightModeWidgetBorderColor
                        }`,
                        overflow: expandWatchlist ? "hidden" : "visible",
                        transition: "none",
                      }}
                      className="hoverShadow"
                    >
                      <Charts
                        handleWatchListAddRemove={handleWatchListAddRemove}
                      />
                    </div>
                  </div>
                }
              />
              <Route
                path="/"
                element={<Trades handleDeleteTrade={handleDeleteTrade} />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
      {showLoginModal && (
        <div
          style={{
            position: "fixed",
            height: "100vh",
            width: "100vw",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            top: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            color: darkMode ? darkModeText : lightModeText,
          }}
          onClick={() => {
            setShowLoginModal?.(false);
          }}
        >
          <div
            style={{
              background: darkMode
                ? darkModeWidgetBackground
                : lightModeWidgetBackground,
              border: `1px solid ${
                darkMode
                  ? darkModeWidgetBorderColor
                  : lightModeWidgetBorderColor
              }`,
              display: "flex",
              justifyItems: "center",
              flexDirection: "column",
              alignItems: "start",
              padding: "15px 20px",
              position: "relative",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                fontSize: getFont?.(10),
                cursor: "pointer",
                padding: "4px",
              }}
              onClick={() => {
                setShowLoginModal?.(false);
              }}
            >
              Close
            </div>
            <div style={{ fontSize: getFont?.(24) }}>Login</div>
            <div style={{ marginTop: "5px" }}>
              <div style={{ fontSize: getFont?.(12) }}>Username</div>
              <input
                autoFocus={loginFields.un !== "" ? false : true}
                autoComplete="username"
                value={loginFields.un}
                style={{
                  fontSize: getFont?.(12),
                  background: "transparent",
                  border: `1px solid ${
                    darkMode ? darkModeBorder : lightModeBorder
                  }`,
                  outline: "none",
                  cursor: "pointer",
                  color: darkMode ? darkModeText : lightModeText,
                }}
                type="text"
                name="un"
                onChange={handleLoginFieldChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLoginIn();
                  }
                }}
              />
            </div>
            <div style={{}}>
              <div style={{ fontSize: getFont?.(12) }}>Password</div>
              <input
                autoFocus={loginFields.un !== "" ? true : false}
                autoComplete="current-password"
                value={loginFields.pw}
                style={{
                  fontSize: getFont?.(12),
                  background: "transparent",
                  border: `1px solid ${
                    darkMode ? darkModeBorder : lightModeBorder
                  }`,
                  outline: "none",
                  cursor: "pointer",
                  color: darkMode ? darkModeText : lightModeText,
                }}
                type="password"
                name="pw"
                onChange={handleLoginFieldChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLoginIn();
                  }
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  margin: "10px 0",
                  fontSize: getFont?.(10),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "2px 6px",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontWeight: accentButtonTextWeight,
                  color: accentButtonText,
                  background: accentButtonBackground,
                }}
                onClick={handleLoginIn}
              >
                <div>Login</div>
              </div>
              &nbsp;
              <div
                style={{
                  margin: "10px 0",
                  fontSize: getFont?.(10),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "2px 6px",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontWeight: accentButtonTextWeight,
                  color: accentButtonText,
                  background: "grey",
                }}
                onClick={handleRegister}
              >
                <div>Register</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
